import type { z } from "zod";
import { problemSchema } from "@shared/contracts";
import { ApiError } from "./api-error";

const API_ROOT = "/api/v1";

type Options = { method?: "GET" | "POST"; body?: unknown; signal?: AbortSignal };

/**
 * Calls the site's own API and checks the answer against its contract. A reply that does
 * not match is treated as a failure, never passed on as if it were data.
 */
export async function request<Schema extends z.ZodType>(
  path: string,
  schema: Schema,
  { method = "GET", body, signal }: Options = {},
): Promise<z.infer<Schema>> {
  let response: Response;
  try {
    response = await fetch(`${API_ROOT}${path}`, {
      method,
      headers: body === undefined ? { accept: "application/json" } : jsonHeaders,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError("network", "The site could not be reached. Check your connection.");
  }
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const problem = problemSchema.safeParse(payload);
    throw problem.success
      ? ApiError.fromProblem(problem.data)
      : new ApiError("unexpected_response", `The site answered with status ${response.status}.`);
  }
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError("unexpected_response", "The site sent an answer this page cannot read.");
  }
  return parsed.data;
}

const jsonHeaders = { accept: "application/json", "content-type": "application/json" };
